import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ConfigProvider from '../config-provider';
import cls from 'classnames';
import Button from '../button';
import Input from '../input';
import Switch from '../switch';
import Balloon from '../balloon';
import { events, KEYCODE } from '../util';

const { Tooltip } = Balloon;

class Modal extends React.Component {
  static propTypes = {
    prefix: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
    /**
     * 是否默认可见，非受控
     */
    defaultVisible: PropTypes.bool,
    /**
     * 是否可见，受控
     */
    visible: PropTypes.bool,
    /**
     * 点击关闭按钮回调
     */
    onClose: PropTypes.func,
    /**
     * Modal标题
     */
    title: PropTypes.string.isRequired,
    /**
     * Modal标题是否可编辑
     */
    titleEditable: PropTypes.bool,
    /**
     * 标题编辑图标提示文案
     */
    titleEditTooltip: PropTypes.string,
    /**
     * 可编辑时标题输入框的宽度
     */
    titleInputWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * 标题编辑回调
     */
    onEditTitleChange: PropTypes.func,
    /**
     * Modal描述
     */
    description: PropTypes.string,
    /**
     * 操作区
     */
    operations: PropTypes.element,
    /**
     * 侧边栏内容
     */
    sideDrawer: PropTypes.node,
    /**
     * 侧边栏内容宽度
     */
    sideDrawerWidth: PropTypes.number,
    /**
     * 侧边栏切换显示Switch前label
     */
    sideDrawerLabel: PropTypes.string,
    /**
     * 侧边栏内容是否可见
     */
    sideDrawerVisible: PropTypes.bool,
    /**
     * 侧边栏内容切换可见回调
     */
    onSideDrawerVisibleChange: PropTypes.func,
    /**
     * 是否支持 esc 按键关闭弹层
     */
    canCloseByEsc: PropTypes.bool,
  }

  static defaultProps = {
    prefix: 'next-',
    sideDrawerWidth: 400,
    titleEditTooltip: '点击即可编辑',
    onClose: () => {},
    onSideDrawerVisibleChange: () => {},
    onEditTitleChange: () => {},
    canCloseByEsc: true,
  }

  constructor(props) {
    super(props);
    this.state = {
      visible: 'visible' in props ? props.visible : (props.defaultVisible || false),
      isEditing: false,
    };
    this.container = document.createElement('div');
  }

  componentDidMount() {
    document.body.appendChild(this.container);
    if (this.state.visible) {
      document.body.classList.add('next-modal-open');
    }
    this.addDocumentEvents();
  }

  addDocumentEvents = () => {
    if (this.props.canCloseByEsc) {
      this._keydownEvents = events.on(document, 'keydown', this.handleDocumentKeyDown);
    }
  }

  handleDocumentKeyDown = (e) => {
    if (this.state.visible && e.keyCode === KEYCODE.ESC) {
      this.setState({
        visible: false,
      });
      this.props.onClose()
    }
  }

  removeDocumentEvents = () => {
    if (this._keydownEvents) {
      this._keydownEvents.off();
      this._keydownEvents = null;
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.visible !== this.props.visible) {
      this.setState({
        visible: nextProps.visible,
      });
    }
  }

  componentDidUpdate() {
    if (this.state.visible === false) {
      document.body.classList.remove('next-modal-open');
    }
  }

  componentWillUnmount() {
    document.body.removeChild(this.container);
    document.body.classList.remove('next-modal-open');
    this.container = null;
    this.removeDocumentEvents();
  }

  onSideDrawerVisibleChange = (checked) => {
    this.props.onSideDrawerVisibleChange(checked);
  }

  onEditTitleChange = (value) => {
    this.props.onEditTitleChange(value);
  }

  onEnterEditing = () => {
    this.setState({
      isEditing: true,
    });
  }

  onLeaveEditing = () => {
    this.setState({
      isEditing: false,
    });
  }

  onClose = () => {
    const { onClose } = this.props;
    if (!('visible' in this.props)) {
      this.setState({
        visible: false,
      });
    }
    onClose();
  }

  renderTitle = () => {
    const { prefix, title, titleEditable, titleEditTooltip, titleInputWidth } = this.props;
    const { isEditing } = this.state;
    const editTitle = isEditing ?
      (<Input
        size="xs"
        autoFocus
        value={title}
        onChange={this.onEditTitleChange}
        onBlur={this.onLeaveEditing}
        onPressEnter={this.onLeaveEditing}
        style={{ width: titleInputWidth }}
      />)
      :
      (
        <span>
          <span className="edit-title">{title}</span>
          <Tooltip
            trigger={
              <Button.Icon
                className={`${prefix}modal-edit-icon`}
                type="secondary"
                color="gray"
                name="pencil"
                onClick={this.onEnterEditing}
              />}
            align="r"
          >
            {titleEditTooltip}
          </Tooltip>
        </span>
      );
    return (
      <div className={`${prefix}modal-title`}>
        {
          titleEditable ? editTitle : title
        }
      </div>
    );
  }

  renderHeader = () => {
    const {
      prefix,
      description,
      operations,
      sideDrawer,
      sideDrawerLabel,
      sideDrawerVisible,
    } = this.props;
    const title = this.renderTitle();
    return (
      <div className={`${prefix}modal-header`}>
        <div className="left-part">
          { title }
          <div className={`${prefix}modal-description`}>
            { description }
          </div>
        </div>
        <div className="right-part">
          <div className={`${prefix}modal-operations`}>{operations}</div>
          {
            sideDrawer ?
              <div className={`${prefix}modal-side-drawer-switch`}>
                <span className={`${prefix}modal-side-drawer-switch-label`}>{sideDrawerLabel}</span>
                <Switch size="small" checked={sideDrawerVisible} onChange={this.onSideDrawerVisibleChange} />
              </div> : null
          }
          <Button.Icon className={`${prefix}modal-close`} name="remove" color="gray" onClick={this.onClose} />
        </div>
      </div>
    );
  }

  renderBody = () => {
    const { prefix, sideDrawer, sideDrawerVisible, sideDrawerWidth, children } = this.props;
    return (
      <div className={`${prefix}modal-body`}>
        <div className={`${prefix}modal-children`}>{children}</div>
        {
          sideDrawer ?
            <div
              className={cls({
                [`${prefix}modal-side-drawer`]: true,
                [`${prefix}modal-side-drawer-visible`]: sideDrawerVisible,
              })}
              style={{
                width: sideDrawerVisible ? sideDrawerWidth : 0,
              }}
            >
              {sideDrawer}
            </div> : null
        }
      </div>
    );
  }

  renderModal = () => {
    const { prefix, className, style } = this.props;
    const header = this.renderHeader();
    const body = this.renderBody();
    return (
      <div
        className={cls({
          [`${prefix}modal`]: true,
          [className]: !!className,
        })}
        style={style}
      >
        { header }
        { body }
      </div>
    );
  }

  render() {
    const { visible } = this.state;
    if (visible) {
      return ReactDOM.createPortal(
        this.renderModal(),
        this.container
      );
    }
    return null;
  }
}

export default ConfigProvider.config(Modal);
